import { useRef, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Upload, Loader2, X, Check } from "lucide-react";
import { api } from "@/lib/api";
import { resolveImageUrl } from "@/components/ImageUpload";

const TYPES = [
  { k: "college",  l: "College / University" },
  { k: "school",   l: "School (K-12)" },
  { k: "edtech",   l: "EdTech Company" },
  { k: "supplier", l: "Supplier" },
];

const MAX_IMG_KB = 500;
const MAX_LOGO_KB = 250;

const INPUT_CLS = "w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm bg-white";

// Defined OUTSIDE the page component so React does not remount it — and
// its <input> children — on every keystroke. Keeping it inside caused every
// keystroke to lose focus after the first character.
function Field({ label, required, span, children }) {
  return (
    <label className={`block ${span || ""}`}>
      <span className="text-xs font-semibold text-slate-700 mb-1 block">
        {label}{required && <span className="text-red-500"> *</span>}
      </span>
      {children}
    </label>
  );
}

export default function AddMyProfilePage() {
  const [f, setF] = useState({
    profile_type: "college",
    name: "", address: "", city: "", pin: "", state: "",
    phone: "", email: "", website: "",
    logo: "", images: [],
    description: "",
    courses_offered: "", fees: "", head_of_institution: "",
    tariff_accepted: false, payment_mode: "",
    submitter_name: "", submitter_phone: "", submitter_email: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingImgIdx, setUploadingImgIdx] = useState(-1);
  const logoRef = useRef(null);
  const imgRefs = [useRef(null), useRef(null), useRef(null)];

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const showCollegeFields = f.profile_type === "college";
  const showSchoolFields  = f.profile_type === "school";
  const showCourses = showCollegeFields;
  const showFees    = showCollegeFields || showSchoolFields;
  const showHead    = showCollegeFields || showSchoolFields;

  const uploadFile = async (file, maxKb) => {
    if (file.size > maxKb * 1024) {
      throw new Error(`File too large. Max ${maxKb} KB.`);
    }
    const okExts = /\.(jpe?g|png|gif)$/i;
    if (!okExts.test(file.name || "")) {
      throw new Error("Only JPG, JPEG, PNG or GIF allowed.");
    }
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/profile-submissions/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const onLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try { set("logo", await uploadFile(file, MAX_LOGO_KB)); toast.success("Logo uploaded"); }
    catch (e) { toast.error(e.message || "Upload failed"); }
    finally { setUploadingLogo(false); if (logoRef.current) logoRef.current.value = ""; }
  };

  const onImg = async (idx, file) => {
    if (!file) return;
    setUploadingImgIdx(idx);
    try {
      const url = await uploadFile(file, MAX_IMG_KB);
      const next = [...f.images];
      next[idx] = url;
      set("images", next.filter(Boolean));
      toast.success(`Image ${idx + 1} uploaded`);
    } catch (e) { toast.error(e.message || "Upload failed"); }
    finally { setUploadingImgIdx(-1); if (imgRefs[idx].current) imgRefs[idx].current.value = ""; }
  };

  const removeImg = (idx) => {
    const next = [...f.images];
    next.splice(idx, 1);
    set("images", next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    if (!f.submitter_name.trim() || !f.submitter_email.trim()) { toast.error("Submitter name & email are required"); return; }
    if (!f.tariff_accepted) { toast.error("Please accept the Annual Profile Listing Tariff to continue"); return; }
    if (!f.payment_mode) { toast.error("Please select a payment mode"); return; }
    setBusy(true);
    try {
      await api.post("/profile-submissions", f);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="add-my-profile-done">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 mb-6">
          <Check className="w-8 h-8"/>
        </div>
        <h1 className="font-editorial text-4xl font-bold text-slate-900 mb-3">Thanks — we got it.</h1>
        <p className="text-slate-600 max-w-md mx-auto">
          Your profile submission has been sent to the EduTime editorial team. We usually review new
          submissions within 2–3 working days and will email you once your profile goes live.
        </p>
      </div>
    );
  }

  const Field2 = null; // (Field is defined at module scope — see above.)
  void Field2;

  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid="add-my-profile-page">
      <div className="flex items-center gap-2 text-amber-700 mb-2">
        <UserPlus className="w-4 h-4"/><span className="overline">Directory</span>
      </div>
      <h1 className="headline text-4xl md:text-5xl font-bold text-slate-900 mb-3">Add My Profile</h1>
      <p className="text-slate-600 max-w-2xl mb-8">
        Submit your college, school, EdTech company or supplier for inclusion in the EduTime Directory.
        Our editors will review and publish approved submissions.
      </p>

      <form onSubmit={submit} className="space-y-8 card-soft p-6 md:p-8">
        {/* Profile type */}
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-2">I'm submitting a:</div>
          <div className="flex flex-wrap gap-2">
            {TYPES.map(t => (
              <button key={t.k} type="button" onClick={() => set("profile_type", t.k)}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${f.profile_type === t.k ? "bg-slate-900 text-yellow-300 border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-sky-400"}`}
                data-testid={`amp-type-${t.k}`}>{t.l}</button>
            ))}
          </div>
        </div>

        {/* Institution details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name" required span="md:col-span-2">
            <input required value={f.name} onChange={e => set("name", e.target.value)} className={INPUT_CLS} data-testid="amp-name"/>
          </Field>
          <Field label="Address" span="md:col-span-2">
            <input value={f.address} onChange={e => set("address", e.target.value)} className={INPUT_CLS} data-testid="amp-address"/>
          </Field>
          <Field label="City">
            <input value={f.city} onChange={e => set("city", e.target.value)} className={INPUT_CLS} data-testid="amp-city"/>
          </Field>
          <Field label="State">
            <input value={f.state} onChange={e => set("state", e.target.value)} className={INPUT_CLS} data-testid="amp-state"/>
          </Field>
          <Field label="PIN">
            <input value={f.pin} onChange={e => set("pin", e.target.value)} className={INPUT_CLS} data-testid="amp-pin"/>
          </Field>
          <Field label="Phone">
            <input type="tel" value={f.phone} onChange={e => set("phone", e.target.value)} className={INPUT_CLS} data-testid="amp-phone"/>
          </Field>
          <Field label="Email">
            <input type="email" value={f.email} onChange={e => set("email", e.target.value)} className={INPUT_CLS} data-testid="amp-email"/>
          </Field>
          <Field label="Website" span="md:col-span-2">
            <input value={f.website} onChange={e => set("website", e.target.value)} placeholder="https://..." className={INPUT_CLS} data-testid="amp-website"/>
          </Field>
        </div>

        {/* Annual Profile Listing Tariff */}
        <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="amp-tariff-wrap">
          <input
            type="checkbox"
            checked={!!f.tariff_accepted}
            onChange={e => set("tariff_accepted", e.target.checked)}
            data-testid="amp-tariff"
          />
          Annual Profile Listing Tariff — ₹8,000 / year <span className="text-red-500">*</span>
        </label>

        {/* Logo upload (250 KB) */}
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-2">Logo — JPG / JPEG / PNG / GIF (max 250 KB)</div>
          <input ref={logoRef} type="file" accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif" className="hidden" onChange={e => onLogo(e.target.files?.[0])} data-testid="amp-logo-input"/>
          {f.logo ? (
            <div className="relative w-32 h-32 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
              <img src={resolveImageUrl(f.logo)} alt="logo" className="w-full h-full object-contain"/>
              <button type="button" onClick={() => set("logo", "")} className="absolute top-1 right-1 p-1 rounded-full bg-slate-900/70 text-white"><X className="w-3 h-3"/></button>
            </div>
          ) : (
            <button type="button" onClick={() => logoRef.current?.click()} disabled={uploadingLogo}
              className="w-32 h-32 rounded-2xl border-2 border-dashed border-slate-200 hover:border-sky-400 hover:bg-sky-50/40 flex flex-col items-center justify-center gap-1 text-slate-500 text-xs"
              data-testid="amp-logo-btn">
              {uploadingLogo ? <Loader2 className="w-5 h-5 animate-spin"/> : <Upload className="w-5 h-5"/>}
              {uploadingLogo ? "Uploading…" : "Upload logo"}
            </button>
          )}
        </div>

        {/* Images upload (500 KB × 3) */}
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-2">Photos — JPG / JPEG / PNG / GIF (up to 3, max 500 KB each)</div>
          <div className="grid grid-cols-3 gap-3">
            {[0, 1, 2].map(idx => {
              const url = f.images[idx];
              return (
                <div key={idx} className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
                  <input ref={imgRefs[idx]} type="file" accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif" className="hidden" onChange={e => onImg(idx, e.target.files?.[0])} data-testid={`amp-img-${idx}-input`}/>
                  {url ? (
                    <>
                      <img src={resolveImageUrl(url)} alt={`img ${idx + 1}`} className="w-full h-full object-cover"/>
                      <button type="button" onClick={() => removeImg(idx)} className="absolute top-1 right-1 p-1 rounded-full bg-slate-900/70 text-white"><X className="w-3 h-3"/></button>
                    </>
                  ) : (
                    <button type="button" onClick={() => imgRefs[idx].current?.click()} disabled={uploadingImgIdx === idx}
                      className="w-full h-full flex flex-col items-center justify-center gap-1 text-slate-500 text-xs hover:bg-sky-50/40"
                      data-testid={`amp-img-${idx}-btn`}>
                      {uploadingImgIdx === idx ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
                      {uploadingImgIdx === idx ? "…" : `Photo ${idx + 1}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Description */}
        <Field label="Description" span="md:col-span-2">
          <textarea rows={5} value={f.description} onChange={e => set("description", e.target.value)}
            placeholder="Tell us about your institution / company…"
            className={`${INPUT_CLS} min-h-[120px]`} data-testid="amp-description"/>
        </Field>

        {/* Type-specific fields */}
        {showCourses && (
          <Field label="Courses Offered">
            <textarea rows={3} value={f.courses_offered} onChange={e => set("courses_offered", e.target.value)}
              placeholder="e.g. B.Tech CSE, MBA, MBBS, Integrated PhD…"
              className={`${INPUT_CLS} min-h-[80px]`} data-testid="amp-courses"/>
          </Field>
        )}
        {showFees && (
          <Field label="Fees">
            <textarea rows={2} value={f.fees} onChange={e => set("fees", e.target.value)}
              placeholder="Annual / per-semester fee structure"
              className={`${INPUT_CLS} min-h-[60px]`} data-testid="amp-fees"/>
          </Field>
        )}
        {showHead && (
          <Field label="Head of Institution">
            <input value={f.head_of_institution} onChange={e => set("head_of_institution", e.target.value)}
              placeholder="Principal / Director / Vice-Chancellor name"
              className={INPUT_CLS} data-testid="amp-head"/>
          </Field>
        )}

        {/* Submitter block */}
        <div className="pt-6 border-t border-slate-100">
          <div className="text-xs font-bold uppercase tracking-widest text-amber-700 mb-3">Your details (form submitter)</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Name" required>
              <input required value={f.submitter_name} onChange={e => set("submitter_name", e.target.value)} className={INPUT_CLS} data-testid="amp-submitter-name"/>
            </Field>
            <Field label="Phone">
              <input type="tel" value={f.submitter_phone} onChange={e => set("submitter_phone", e.target.value)} className={INPUT_CLS} data-testid="amp-submitter-phone"/>
            </Field>
            <Field label="Email" required>
              <input required type="email" value={f.submitter_email} onChange={e => set("submitter_email", e.target.value)} className={INPUT_CLS} data-testid="amp-submitter-email"/>
            </Field>
          </div>
        </div>

        {/* Select Payment Mode */}
        <div className="pt-6 border-t border-slate-100" data-testid="amp-payment-section">
          <div className="text-xs font-bold uppercase tracking-widest text-amber-700 mb-3">Select Payment Mode <span className="text-red-500">*</span></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label
              className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-colors ${f.payment_mode === "pay_now" ? "border-emerald-600 bg-emerald-50" : "border-slate-200 bg-white hover:border-sky-400"}`}
              data-testid="amp-payment-pay-now"
            >
              <input
                type="radio"
                name="payment_mode"
                value="pay_now"
                checked={f.payment_mode === "pay_now"}
                onChange={() => set("payment_mode", "pay_now")}
                className="mt-1 w-4 h-4 accent-emerald-600"
              />
              <div className="min-w-0">
                <div className="font-bold text-slate-900 text-sm">Pay now — UPI / Credit Card</div>
                <div className="text-xs text-slate-600 mt-1">Instant payment via Razorpay. You'll be able to open the secure payment page below after selecting this option.</div>
                {f.payment_mode === "pay_now" && (
                  <a
                    href="https://rzp.io/rzp/wYzGykMb"
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-block mt-3 px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-bold shadow-sm hover:-translate-y-0.5 transition-transform"
                    data-testid="amp-razorpay-link"
                  >
                    Open Razorpay to Pay ₹8,000 →
                  </a>
                )}
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-colors ${f.payment_mode === "invoice" ? "border-emerald-600 bg-emerald-50" : "border-slate-200 bg-white hover:border-sky-400"}`}
              data-testid="amp-payment-invoice"
            >
              <input
                type="radio"
                name="payment_mode"
                value="invoice"
                checked={f.payment_mode === "invoice"}
                onChange={() => set("payment_mode", "invoice")}
                className="mt-1 w-4 h-4 accent-emerald-600"
              />
              <div className="min-w-0">
                <div className="font-bold text-slate-900 text-sm">Send me an Invoice</div>
                <div className="text-xs text-slate-600 mt-1">
                  Our accounts team will email a GST invoice with bank / cheque payment details. Your listing will go live once payment clears.
                </div>
              </div>
            </label>
          </div>
        </div>

        <div className="flex justify-end pt-2 border-t border-slate-100">
          <button type="submit" disabled={busy}
            className="px-8 py-3 rounded-full bg-slate-900 text-yellow-300 font-bold text-sm hover:-translate-y-0.5 transition-transform disabled:opacity-60"
            data-testid="amp-submit">
            {busy ? "Submitting…" : "Submit Profile"}
          </button>
        </div>
      </form>
    </div>
  );
}
